/**
 * ElectricRates - SUPERADMIN settings page for managing energy rate profiles
 *
 * Features:
 * - List all rate profiles with filtering
 * - Create/Edit/Delete rate profiles
 * - Lock/unlock to prevent accidental changes
 * - View assignment counts (companies, groups, units)
 * - Manage utility providers (unified tab with shared controls)
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Zap,
  Plus,
  Search,
  Lock,
  Unlock,
  Pencil,
  Trash2,
  Building2,
  Layers,
  Truck,
  AlertCircle,
  RefreshCw,
  Star,
  Filter,
  Archive,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AdminDataTable, type Column, type Action } from '@/components/settings/AdminDataTable';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { RateFormDrawer } from '@/components/settings/RateFormDrawer';
import { UtilityProvidersTab } from '@/components/settings/UtilityProvidersTab';
import { UtilityProviderFormDrawer } from '@/components/settings/UtilityProviderFormDrawer';
import { AssignedRatesTab } from '@/components/settings/AssignedRatesTab';
import { AuditLogLink } from '@/components/settings/AuditLogLink';
import { useEnergyRates } from '@/hooks/useEnergyRates';
import {
  useUtilityProviders,
  useFilteredProviders,
  useUtilityProviderBulkOperations,
} from '@/hooks/useUtilityProviders';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { formatRatePerKwh } from '@/types/rates';
import type { EnergyRate, RateFormData, RateAssignmentCounts } from '@/types/rates';
import type { UtilityProvider, UtilityProviderFormData, CountryCode } from '@/types/utilityProviders';
import { cn } from '@/lib/utils';

// Country/Region configuration for filter dropdowns
const COUNTRIES: Record<string, string> = {
  US: 'United States',
  CA: 'Canada',
  UK: 'United Kingdom',
};

const REGIONS_BY_COUNTRY: Record<string, Record<string, string>> = {
  US: {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
    MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
    OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
    VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  },
  CA: {
    AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
    NL: 'Newfoundland', NS: 'Nova Scotia', NT: 'Northwest Territories', NU: 'Nunavut',
    ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan', YT: 'Yukon',
  },
  UK: {
    London: 'London', Scotland: 'Scotland', Wales: 'Wales', NorthernIreland: 'Northern Ireland',
    England: 'England',
  },
};

export function ElectricRates() {
  const { is_superadmin } = useSettingsContext();

  // Active tab state for unified controls
  const [activeTab, setActiveTab] = useState<'rates' | 'providers' | 'assigned'>('rates');

  // Rate hooks
  const {
    rates,
    isLoading,
    isSubmitting,
    fetchRates,
    createRate,
    updateRate,
    deleteRate,
    toggleLock,
    getAllAssignments,
    refresh,
  } = useEnergyRates();

  // Provider hooks
  const [viewArchived, setViewArchived] = useState(false);
  const {
    providers,
    isLoading: providersLoading,
    isSubmitting: providersSubmitting,
    createProvider,
    updateProvider,
    softDelete: softDeleteProvider,
    restore: restoreProvider,
    hardDelete: hardDeleteProvider,
    refresh: refreshProviders,
  } = useUtilityProviders({
    autoFetch: true,
    includeDeleted: viewArchived,
  });

  const { bulkOperation, isSubmitting: isBulkSubmitting } = useUtilityProviderBulkOperations();

  // Rate state
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<EnergyRate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rateToDelete, setRateToDelete] = useState<EnergyRate | null>(null);
  const [assignments, setAssignments] = useState<Record<string, RateAssignmentCounts>>({});

  // Provider state
  const [providerSearchQuery, setProviderSearchQuery] = useState('');
  const [providerCountryFilter, setProviderCountryFilter] = useState<CountryCode | ''>('');
  const [providerDrawerOpen, setProviderDrawerOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<UtilityProvider | null>(null);
  const [providerDeleteDialogOpen, setProviderDeleteDialogOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<UtilityProvider | null>(null);
  const [hardDeleteMode, setHardDeleteMode] = useState(false);

  // Assigned rates state
  const [assignedSearchQuery, setAssignedSearchQuery] = useState('');
  const [, setAssignmentDrawerOpen] = useState(false);
  const [assignedEntityType, setAssignedEntityType] = useState<string>('');
  const [assignedRateFilter, setAssignedRateFilter] = useState<string>('');
  const [assignedStatusFilter, setAssignedStatusFilter] = useState<string>('');
  const [assignedRefreshKey, setAssignedRefreshKey] = useState(0);

  // Filter providers client-side
  const filteredProviders = useFilteredProviders(providers, {
    search: providerSearchQuery,
    countryCode: providerCountryFilter || undefined,
  });

  // Show only active or only archived based on toggle
  const displayProviders = viewArchived
    ? filteredProviders.filter(p => p.isDeleted)
    : filteredProviders.filter(p => !p.isDeleted);

  // Fetch assignments when rates load
  useEffect(() => {
    if (rates.length > 0) {
      getAllAssignments().then(setAssignments);
    }
  }, [rates, getAllAssignments]);

  // Reset region when country changes
  useEffect(() => {
    setRegionFilter('');
  }, [countryFilter]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRates({
        page: 1,
        search: searchQuery,
        country: countryFilter,
        state: regionFilter,
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, countryFilter, regionFilter]);

  // Access check
  if (!is_superadmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="text-muted-foreground">
          Electric rate management requires SUPERADMIN access.
        </p>
      </div>
    );
  }

  const handleCreateClick = () => {
    setEditingRate(null);
    setDrawerOpen(true);
  };

  const handleEditClick = (rate: EnergyRate) => {
    if (rate.isLocked) {
      return; // Locked rates cannot be edited
    }
    setEditingRate(rate);
    setDrawerOpen(true);
  };

  const handleDeleteClick = (rate: EnergyRate) => {
    if (rate.isLocked || rate.isSystemDefault) {
      return; // Cannot delete locked or system default
    }
    setRateToDelete(rate);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: RateFormData) => {
    if (editingRate) {
      const result = await updateRate(editingRate.id, data);
      if (result) {
        setDrawerOpen(false);
        setEditingRate(null);
      }
    } else {
      const result = await createRate(data);
      if (result) {
        setDrawerOpen(false);
      }
    }
    // Refresh assignments after changes
    getAllAssignments().then(setAssignments);
  };

  const handleDeleteConfirm = async () => {
    if (!rateToDelete) return;
    const success = await deleteRate(rateToDelete.id);
    if (success) {
      setDeleteDialogOpen(false);
      setRateToDelete(null);
    }
  };

  const handleToggleLock = async (rate: EnergyRate) => {
    await toggleLock(rate.id, !rate.isLocked);
  };

  // Provider handlers
  const handleCreateProviderClick = useCallback(() => {
    setEditingProvider(null);
    setProviderDrawerOpen(true);
  }, []);

  const handleEditProviderClick = useCallback((provider: UtilityProvider) => {
    setEditingProvider(provider);
    setProviderDrawerOpen(true);
  }, []);

  const handleArchiveProviderClick = useCallback((provider: UtilityProvider) => {
    setProviderToDelete(provider);
    setHardDeleteMode(false);
    setProviderDeleteDialogOpen(true);
  }, []);

  const handleHardDeleteProviderClick = useCallback((provider: UtilityProvider) => {
    setProviderToDelete(provider);
    setHardDeleteMode(true);
    setProviderDeleteDialogOpen(true);
  }, []);

  const handleRestoreProviderClick = useCallback(async (provider: UtilityProvider) => {
    await restoreProvider(provider.id);
    refreshProviders();
  }, [restoreProvider, refreshProviders]);

  const handleProviderFormSubmit = useCallback(async (data: UtilityProviderFormData) => {
    if (editingProvider) {
      const result = await updateProvider(editingProvider.id, data);
      if (result) {
        setProviderDrawerOpen(false);
        setEditingProvider(null);
      }
    } else {
      const result = await createProvider(data);
      if (result) {
        setProviderDrawerOpen(false);
      }
    }
  }, [editingProvider, updateProvider, createProvider]);

  const handleProviderDeleteConfirm = useCallback(async () => {
    if (!providerToDelete) return;

    let success: boolean;
    if (hardDeleteMode) {
      success = await hardDeleteProvider(providerToDelete.id);
    } else {
      success = await softDeleteProvider(providerToDelete.id);
    }

    if (success) {
      setProviderDeleteDialogOpen(false);
      setProviderToDelete(null);
      refreshProviders();
    }
  }, [providerToDelete, hardDeleteMode, hardDeleteProvider, softDeleteProvider, refreshProviders]);

  const handleBulkArchive = useCallback(async (items: UtilityProvider[]) => {
    const ids = items.map(p => p.id);
    await bulkOperation('softDelete', ids);
    refreshProviders();
  }, [bulkOperation, refreshProviders]);

  const handleBulkRestore = useCallback(async (items: UtilityProvider[]) => {
    const ids = items.map(p => p.id);
    await bulkOperation('restore', ids);
    refreshProviders();
  }, [bulkOperation, refreshProviders]);

  const handleBulkHardDelete = useCallback(async (items: UtilityProvider[]) => {
    const ids = items.map(p => p.id);
    await bulkOperation('hardDelete', ids);
    refreshProviders();
  }, [bulkOperation, refreshProviders]);

  const getAssignmentCount = (rateId: string) => {
    return assignments[rateId]?.totalCount || 0;
  };

  const getAssignmentTooltip = (rateId: string) => {
    const a = assignments[rateId];
    if (!a) return 'No assignments';
    const parts = [];
    if (a.companyCount > 0) parts.push(`${a.companyCount} companies`);
    if (a.groupCount > 0) parts.push(`${a.groupCount} groups`);
    if (a.unitCount > 0) parts.push(`${a.unitCount} units`);
    return parts.length > 0 ? parts.join(', ') : 'No assignments';
  };

  // Table columns
  const columns: Column<EnergyRate>[] = [
    {
      key: 'name',
      label: 'Name',
      width: '220px',
      render: (rate) => (
        <div className="flex items-center gap-2">
          {rate.isSystemDefault && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                </TooltipTrigger>
                <TooltipContent>System Default</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <div className="min-w-0">
            <div className="font-medium truncate">{rate.name}</div>
            {rate.description && (
              <div className="text-xs text-muted-foreground truncate">
                {rate.description}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'gridImportRate',
      label: 'Import',
      width: '85px',
      render: (rate) => (
        <span className="font-mono text-sm whitespace-nowrap">{formatRatePerKwh(rate.gridImportRate)}</span>
      ),
    },
    {
      key: 'gridSellRate',
      label: 'Sell',
      width: '85px',
      render: (rate) => (
        <span className="font-mono text-sm whitespace-nowrap">{formatRatePerKwh(rate.gridSellRate)}</span>
      ),
    },
    {
      key: 'stateProvince',
      label: 'Region',
      width: '95px',
      render: (rate) => (
        rate.stateProvince ? (
          <Badge variant="outline" className="font-normal whitespace-nowrap">
            {rate.countryCode ? `${rate.countryCode}-` : ''}{rate.stateProvince}
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      ),
    },
    {
      key: 'utilityProvider',
      label: 'Provider',
      width: '140px',
      render: (rate) => (
        <span className="text-sm truncate block">
          {rate.utilityProvider || <span className="text-muted-foreground">-</span>}
        </span>
      ),
    },
    {
      key: 'assigned',
      label: 'Assigned',
      width: '90px',
      align: 'center',
      render: (rate) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <div className="flex items-center justify-center gap-1">
                {getAssignmentCount(rate.id) > 0 ? (
                  <>
                    <span className="text-sm font-medium">
                      {getAssignmentCount(rate.id)}
                    </span>
                    <div className="flex gap-0.5 text-muted-foreground">
                      {assignments[rate.id]?.companyCount > 0 && (
                        <Building2 className="h-3 w-3" />
                      )}
                      {assignments[rate.id]?.groupCount > 0 && (
                        <Layers className="h-3 w-3" />
                      )}
                      {assignments[rate.id]?.unitCount > 0 && (
                        <Truck className="h-3 w-3" />
                      )}
                    </div>
                  </>
                ) : (
                  <span className="text-muted-foreground text-sm">0</span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>{getAssignmentTooltip(rate.id)}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      key: 'isLocked',
      label: 'Status',
      width: '90px',
      align: 'center',
      render: (rate) => (
        rate.isLocked ? (
          <Badge variant="secondary" className="gap-1 whitespace-nowrap">
            <Lock className="h-3 w-3" />
            Locked
          </Badge>
        ) : (
          <Badge variant="outline" className="gap-1 text-muted-foreground whitespace-nowrap">
            <Unlock className="h-3 w-3" />
            Open
          </Badge>
        )
      ),
    },
  ];

  // Table actions
  const actions: Action<EnergyRate>[] = [
    {
      label: 'Edit',
      icon: Pencil,
      onClick: handleEditClick,
      show: (rate) => !rate.isLocked,
    },
    {
      label: 'Lock',
      icon: Lock,
      onClick: (rate) => handleToggleLock(rate),
      show: (rate) => !rate.isLocked,
    },
    {
      label: 'Unlock',
      icon: Unlock,
      onClick: (rate) => handleToggleLock(rate),
      show: (rate) => rate.isLocked,
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: handleDeleteClick,
      variant: 'destructive',
      show: (rate) => !rate.isLocked && !rate.isSystemDefault && getAssignmentCount(rate.id) === 0,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="h-6 w-6 text-[#8FB560]" />
            Electric Rates
            <AuditLogLink
              resourceTypes={['rate_profile', 'rate_assignment', 'utility_provider']}
              tooltip="View rates audit history"
            />
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage energy rate profiles and utility providers
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {/* Search input - left of action button */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={
                activeTab === 'rates' ? "Search rates..." :
                activeTab === 'providers' ? "Search providers..." :
                "Search assignments..."
              }
              value={
                activeTab === 'rates' ? searchQuery :
                activeTab === 'providers' ? providerSearchQuery :
                assignedSearchQuery
              }
              onChange={e => {
                if (activeTab === 'rates') setSearchQuery(e.target.value);
                else if (activeTab === 'providers') setProviderSearchQuery(e.target.value);
                else setAssignedSearchQuery(e.target.value);
              }}
              className="pl-9 w-[180px]"
            />
          </div>
          {/* Add button - context-aware */}
          {activeTab === 'rates' && (
            <Button
              size="sm"
              onClick={handleCreateClick}
              className="bg-[#8FB560] hover:bg-[#7a9d4e]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Rate
            </Button>
          )}
          {activeTab === 'providers' && !viewArchived && (
            <Button
              size="sm"
              onClick={handleCreateProviderClick}
              className="bg-[#8FB560] hover:bg-[#7a9d4e]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Provider
            </Button>
          )}
          {activeTab === 'assigned' && (
            <Button
              size="sm"
              onClick={() => setAssignmentDrawerOpen(true)}
              className="bg-[#8FB560] hover:bg-[#7a9d4e]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Assignment
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'rates' | 'providers' | 'assigned')} className="w-full">
        {/* Tabs row with filters on right */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <TabsList className="bg-card border border-border p-1">
            <TabsTrigger value="rates" className="data-[state=active]:bg-brand data-[state=active]:text-brand-foreground gap-2">
              <Zap className="h-4 w-4" />
              Rate Profiles
            </TabsTrigger>
            <TabsTrigger value="providers" className="data-[state=active]:bg-brand data-[state=active]:text-brand-foreground gap-2">
              <Building2 className="h-4 w-4" />
              Utility Providers
            </TabsTrigger>
            <TabsTrigger value="assigned" className="data-[state=active]:bg-brand data-[state=active]:text-brand-foreground gap-2">
              <Layers className="h-4 w-4" />
              Assigned Rates
            </TabsTrigger>
          </TabsList>

          {/* Filters - change based on active tab */}
          <div className="flex flex-wrap gap-2 items-center">
            {/* Rates tab filters */}
            {activeTab === 'rates' && (
              <>
                <Select
                  value={countryFilter || 'all'}
                  onValueChange={v => setCountryFilter(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="All countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All countries</SelectItem>
                    {Object.entries(COUNTRIES).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={regionFilter || 'all'}
                  onValueChange={v => setRegionFilter(v === 'all' ? '' : v)}
                  disabled={!countryFilter}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder={countryFilter ? 'All regions' : 'Select country'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All regions</SelectItem>
                    {countryFilter && REGIONS_BY_COUNTRY[countryFilter] &&
                      Object.entries(REGIONS_BY_COUNTRY[countryFilter]).map(([code, name]) => (
                        <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </>
            )}

            {/* Providers tab filters */}
            {activeTab === 'providers' && (
              <>
                <Select
                  value={providerCountryFilter || 'all'}
                  onValueChange={v => setProviderCountryFilter(v === 'all' ? '' : v as CountryCode)}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="All countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All countries</SelectItem>
                    {Object.entries(COUNTRIES).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant={viewArchived ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => setViewArchived(!viewArchived)}
                  className={cn(viewArchived && 'bg-orange-500/10 text-orange-500 border-orange-500/20')}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {viewArchived ? 'Viewing Archived' : 'View Archived'}
                </Button>
              </>
            )}

            {/* Assigned tab filters */}
            {activeTab === 'assigned' && (
              <>
                <Select
                  value={assignedEntityType || 'all'}
                  onValueChange={v => setAssignedEntityType(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="company">Companies</SelectItem>
                    <SelectItem value="group">Groups</SelectItem>
                    <SelectItem value="unit">Units</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={assignedRateFilter || 'all'}
                  onValueChange={v => setAssignedRateFilter(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="All rates" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All rates</SelectItem>
                    {rates.map((rate) => (
                      <SelectItem key={rate.id} value={rate.id}>
                        {rate.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={assignedStatusFilter || 'all'}
                  onValueChange={v => setAssignedStatusFilter(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="All status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="future">Future</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}

            {/* Refresh button - icon only */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (activeTab === 'rates') refresh();
                else if (activeTab === 'providers') refreshProviders();
                else setAssignedRefreshKey(k => k + 1);
              }}
              disabled={activeTab === 'rates' ? isLoading : activeTab === 'providers' ? providersLoading : false}
              className="h-9 w-9"
            >
              <RefreshCw className={cn("h-4 w-4",
                (activeTab === 'rates' ? isLoading : activeTab === 'providers' ? providersLoading : false) && "animate-spin"
              )} />
            </Button>
          </div>
        </div>

        {/* Rates Tab Content */}
        <TabsContent value="rates" className="mt-6">
          <AdminDataTable<EnergyRate>
            data={rates}
            columns={columns}
            actions={actions}
            loading={isLoading}
            emptyMessage="No rate profiles found"
            onRowClick={(rate) => !rate.isLocked && handleEditClick(rate)}
            tableId="energy-rates"
            minRows={10}
            itemLabel="rate"
          />
        </TabsContent>

        {/* Utility Providers Tab Content */}
        <TabsContent value="providers" className="mt-6">
          <UtilityProvidersTab
            providers={displayProviders}
            isLoading={providersLoading}
            viewArchived={viewArchived}
            onEdit={handleEditProviderClick}
            onArchive={handleArchiveProviderClick}
            onHardDelete={handleHardDeleteProviderClick}
            onRestore={handleRestoreProviderClick}
            onBulkArchive={handleBulkArchive}
            onBulkRestore={handleBulkRestore}
            onBulkHardDelete={handleBulkHardDelete}
            isBulkSubmitting={isBulkSubmitting}
          />
        </TabsContent>

        {/* Assigned Rates Tab Content */}
        <TabsContent value="assigned" className="mt-6">
          <AssignedRatesTab
            rates={rates}
            searchQuery={assignedSearchQuery}
            entityTypeFilter={assignedEntityType}
            rateFilter={assignedRateFilter}
            statusFilter={assignedStatusFilter}
            refreshKey={assignedRefreshKey}
            onAssignmentCreated={() => {
              setAssignmentDrawerOpen(false);
              getAllAssignments().then(setAssignments);
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Rate Profile</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{rateToDelete?.name}"?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate Form Drawer (outside tabs so it overlays properly) */}
      <RateFormDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingRate(null);
        }}
        onSubmit={handleFormSubmit}
        rate={editingRate}
        isSubmitting={isSubmitting}
      />

      {/* Provider Form Drawer */}
      <UtilityProviderFormDrawer
        open={providerDrawerOpen}
        onClose={() => {
          setProviderDrawerOpen(false);
          setEditingProvider(null);
        }}
        onSubmit={handleProviderFormSubmit}
        provider={editingProvider}
        isSubmitting={providersSubmitting}
      />

      {/* Provider Delete/Archive Confirmation Dialog */}
      <Dialog open={providerDeleteDialogOpen} onOpenChange={setProviderDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {hardDeleteMode ? (
                <>
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  Permanently Delete Provider
                </>
              ) : (
                <>
                  <Archive className="h-5 w-5 text-orange-500" />
                  Archive Provider
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {hardDeleteMode ? (
                <>
                  Are you sure you want to <strong>permanently delete</strong>{' '}
                  "{providerToDelete?.name}"? This action cannot be undone.
                </>
              ) : (
                <>
                  Are you sure you want to archive "{providerToDelete?.name}"?
                  Archived providers can be restored later from the archived view.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setProviderDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant={hardDeleteMode ? 'destructive' : 'secondary'}
              onClick={handleProviderDeleteConfirm}
              className={cn(!hardDeleteMode && 'bg-orange-500 hover:bg-orange-600 text-white')}
            >
              {hardDeleteMode ? 'Delete Permanently' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
